import { api } from '../client';

// ==================== TypeScript Interfaces ====================

export type DemurrageStatus =
  | 'WITHIN_FREE_TIME'
  | 'FREE_TIME_EXPIRING'
  | 'DEMURRAGE_ACCRUING'
  | 'CONTAINER_RETURNED'
  | 'WAIVED';

export interface DemurrageCalculation {
  shipmentId: string;
  shipmentNumber: string;
  status: DemurrageStatus;
  freeTimeDays: number;
  freeTimeExpiry: Date | null;
  timeRemainingHours: number | null;
  timeRemainingMinutes: number | null;
  demurrageDaysAccrued: number;
  demurrageRatePerDay: number;
  totalDemurrageCharges: number;
  isExpiring: boolean;
  isAccruing: boolean;
  containerReturnDate: Date | null;
}

export interface MarkContainerReturnedDto {
  returnDate: Date;
}

export interface ExpiringFreeTimeResponse {
  total: number;
  critical: number;
  urgent: number;
  shipments: Array<{
    id: string;
    shipmentNumber: string;
    freeTimeExpiry: Date;
    hoursRemaining: number;
  }>;
}

export interface AccruingDemurrageResponse {
  total: number;
  totalCharges: number;
  shipments: Array<{
    id: string;
    shipmentNumber: string;
    demurrageDaysAccrued: number;
    totalDemurrageCharges: number;
  }>;
}

export interface MarkReturnedResponse {
  success: boolean;
  message: string;
  shipment: {
    id: string;
    shipmentNumber: string;
    containerReturnDate: Date;
    totalDemurrageCharges: number;
    demurrageStatus: DemurrageStatus;
  };
}

// ==================== API Methods ====================

export const demurrageApi = {
  /**
   * Calculate real-time demurrage for a specific shipment
   * GET /tms/demurrage/shipment/:id
   */
  async calculateDemurrage(shipmentId: string): Promise<DemurrageCalculation> {
    const response = await api.get<DemurrageCalculation>(`/tms/demurrage/shipment/${shipmentId}`);

    // Convert date strings to Date objects
    if (response.data.freeTimeExpiry) {
      response.data.freeTimeExpiry = new Date(response.data.freeTimeExpiry);
    }
    if (response.data.containerReturnDate) {
      response.data.containerReturnDate = new Date(response.data.containerReturnDate);
    }

    return response.data;
  },

  /**
   * Mark container as returned and finalize demurrage charges
   * POST /tms/demurrage/shipment/:id/return
   */
  async markContainerReturned(
    shipmentId: string,
    dto: MarkContainerReturnedDto
  ): Promise<MarkReturnedResponse> {
    const response = await api.post<MarkReturnedResponse>(
      `/tms/demurrage/shipment/${shipmentId}/return`,
      dto
    );

    // Convert date strings to Date objects
    if (response.data.shipment.containerReturnDate) {
      response.data.shipment.containerReturnDate = new Date(
        response.data.shipment.containerReturnDate
      );
    }

    return response.data;
  },

  /**
   * Get shipments with expiring free time
   * GET /tms/demurrage/expiring?hours=24
   */
  async getExpiringFreeTime(hoursThreshold: number = 24): Promise<ExpiringFreeTimeResponse> {
    const response = await api.get<ExpiringFreeTimeResponse>('/tms/demurrage/expiring', {
      params: { hours: hoursThreshold },
    });

    // Convert date strings to Date objects
    response.data.shipments = response.data.shipments.map((shipment) => ({
      ...shipment,
      freeTimeExpiry: new Date(shipment.freeTimeExpiry),
    }));

    return response.data;
  },

  /**
   * Get shipments currently accruing demurrage charges
   * GET /tms/demurrage/accruing
   */
  async getAccruingDemurrage(): Promise<AccruingDemurrageResponse> {
    const response = await api.get<AccruingDemurrageResponse>('/tms/demurrage/accruing');
    return response.data;
  },
};
